import type { Season } from '../types';
import * as seasonApi from '../api/season';
import { KeyedAsyncCache } from './asyncCache';

const STATUS_TO_BACKEND: Record<Season['status'], seasonApi.SeasonStatus> = {
  'Chuẩn bị': 'PREPARING',
  'Đang canh tác': 'GROWING',
  'Sắp thu hoạch': 'NEAR_HARVEST',
  'Đã thu hoạch': 'HARVESTED',
  'Kết thúc': 'FINISHED',
  'Có rủi ro': 'AT_RISK',
};
const STATUS_FROM_BACKEND: Record<seasonApi.SeasonStatus, Season['status']> = {
  PREPARING: 'Chuẩn bị',
  GROWING: 'Đang canh tác',
  NEAR_HARVEST: 'Sắp thu hoạch',
  HARVESTED: 'Đã thu hoạch',
  FINISHED: 'Kết thúc',
  AT_RISK: 'Có rủi ro',
};

function toFrontendSeason(s: seasonApi.BackendSeason): Season {
  return {
    id: s._id,
    farmerId: s.farmerId,
    fieldId: s.fieldId,
    maMuaVu: s.code || '',
    name: s.name,
    cayTrong: s.mainCrop,
    variety: s.variety || '',
    startDate: s.startDate,
    endDate: s.endDate,
    expectedYield: s.expectedYield,
    unit: s.unit,
    dinhMucVatTu: s.materialNorm,
    status: STATUS_FROM_BACKEND[s.status] || 'Chuẩn bị',
    cropId: s.cropId,
  };
}

const byFarmer = new KeyedAsyncCache<Season[]>(
  async (farmerId) => (await seasonApi.listSeasonsByFarmer(farmerId)).map(toFrontendSeason),
  [],
  'seasons:byFarmer',
);
const byHtx = new KeyedAsyncCache<Season[]>(
  async (htxId) => (await seasonApi.listSeasonsByHtx(htxId)).map(toFrontendSeason),
  [],
  'seasons:byHtx',
);

export function getSeasonsByFarmer(farmerId?: string): Season[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

/** Cross-farmer season list for HTX-side pages (contract/loan/harvest/supply forms
 * that need to reference "which season" regardless of which farmer it belongs to). */
export function getSeasonsByHtx(htxId?: string): Season[] {
  if (!htxId) return [];
  return byHtx.get(htxId);
}

function invalidateBoth(farmerId: string, htxId?: string) {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
  if (htxId) {
    byHtx.invalidate(htxId);
    byHtx.get(htxId);
  }
}

export async function addSeason(data: Omit<Season, 'id'>, htxId?: string): Promise<Season> {
  const created = await seasonApi.createSeason({
    farmerId: data.farmerId,
    fieldId: data.fieldId,
    code: data.maMuaVu,
    name: data.name,
    mainCrop: data.cayTrong,
    cropId: data.cropId,
    variety: data.variety,
    startDate: data.startDate,
    endDate: data.endDate,
    expectedYield: data.expectedYield,
    unit: data.unit,
    materialNorm: data.dinhMucVatTu,
  });
  invalidateBoth(data.farmerId, htxId);
  return toFrontendSeason(created);
}

export async function updateSeason(id: string, data: Partial<Season>, farmerId: string, htxId?: string): Promise<Season> {
  const updated = await seasonApi.updateSeason(id, {
    name: data.name,
    endDate: data.endDate,
    expectedYield: data.expectedYield,
    status: data.status ? STATUS_TO_BACKEND[data.status] : undefined,
    materialNorm: data.dinhMucVatTu,
    cropId: data.cropId,
  });
  invalidateBoth(farmerId, htxId);
  return toFrontendSeason(updated);
}
